import React, { useState } from 'react';
import { Button } from '../LoadingButton';
import { ArrowUpward } from '@styled-icons/material/ArrowUpward';
import { Download } from '@styled-icons/material/Download';
import { Share } from '@styled-icons/material/Share';
import { Undo } from '@styled-icons/material/Undo';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { X } from '@styled-icons/boxicons-regular/X';

const ImageFeedbackButtons = ({ 
  message, 
  onFeedback, 
  size = "normal", 
  isLoading = false,
  feedbackLoading = {},
  className = "",
  onUndo,
  isLastGeneration = false
}) => {
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [copied, setCopied] = useState(false);
  const isLarge = size === "large";
  const iconSize = isLarge ? 24 : 20;
  
  const messageId = message.id;
  const isUpscaleLoading = feedbackLoading[`${messageId}-upscale`];
  const isDownloadLoading = feedbackLoading[`${messageId}-download`];
  
  const handleShare = async (platform) => {
    const imageUrl = message.imageUrl;
    const text = "Check out this amazing interior design I created with Moodly!";
    
    switch(platform) {
      case 'copy':
        try {
          await navigator.clipboard.writeText(imageUrl);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch (err) {
          console.error('Failed to copy link:', err);
        }
        break;
      case 'pinterest':
        const pinterestUrl = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(window.location.href)}&media=${encodeURIComponent(imageUrl)}&description=${encodeURIComponent(text)}`;
        window.open(pinterestUrl, '_blank');
        setShowSharePopup(false);
        break;
      case 'whatsapp':
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + imageUrl)}`;
        window.open(whatsappUrl, '_blank');
        setShowSharePopup(false);
        break;
    }
  };
  
  return (
    <>
      <div className={`${isLarge ? 'flex gap-3 justify-center mt-4' : 'flex flex-wrap gap-2 justify-start mt-2 max-w-full overflow-x-auto'} ${className}`}>
        {/* Undo Button - Only show for last generation */}
        {isLastGeneration && onUndo && (
          <button
            onClick={onUndo}
            disabled={isLoading}
            className={`inline-flex items-center justify-center my-3 ml-1 rounded-full focus:outline-none disabled:opacity-50 text-orange-800 bg-orange-50 hover:bg-orange-100 hover:text-orange-800 whitespace-nowrap ${
              isLarge 
                ? 'px-4 py-3 text-base' 
                : 'px-2 py-1.5 text-[10px] sm:px-3 sm:py-2 sm:text-sm'
            }`}
          >
            <Undo size={isLarge ? 24 : 16} className="mr-1" />
            <span>Undo</span>
          </button>
        )}

        {/* Upscale Button with Text Progress */}
        <button
          onClick={() => onFeedback('upscale', message)}
          disabled={isUpscaleLoading || isLoading || message.feedback?.isUpscaled}
          className={`inline-flex items-center justify-center my-3 ml-1 rounded-full focus:outline-none disabled:opacity-50 text-pink-800 bg-pink-50 hover:bg-pink-100 hover:text-pink-800 whitespace-nowrap ${
            isLarge 
              ? 'px-4 py-3 text-base' 
              : 'px-2 py-1.5 text-[10px] sm:px-3 sm:py-2 sm:text-sm'
          }`}
        >
          <ArrowUpward size={isLarge ? 24 : 16} className="mr-1" />
          <span>
            {isUpscaleLoading 
              ? 'Upscaling...' 
              : message.feedback?.isUpscaled 
                ? 'Upscaled' 
                : 'Upscale'
            }
          </span>
        </button>

        {/* Download Button */}
        <Button
          href={message.imageUrl}
          download={`design-${message.feedback?.isUpscaled ? 'upscaled-' : ''}${message.id || Date.now()}.jpg`}
          target="_blank"
          rel="noopener noreferrer"
          highlight
          onClick={() => onFeedback('download', message)}
          disabled={isDownloadLoading || isLoading}
          className={`whitespace-nowrap ${
            isLarge 
              ? 'px-4 py-3 text-base' 
              : 'px-2 py-1.5 text-[10px] sm:px-3 sm:py-2 sm:text-sm'
          }`}
        >
          <Download size={isLarge ? 24 : 16} className="mr-1" />
          Download
        </Button>

        {/* Share Button */}
        <Button
          onClick={() => setShowSharePopup(true)}
          disabled={isLoading}
          highlight
          className={`whitespace-nowrap ${
            isLarge 
              ? 'px-4 py-3 text-base' 
              : 'px-2 py-1.5 text-[10px] sm:px-3 sm:py-2 sm:text-sm'
          }`}
        >
          <Share size={isLarge ? 24 : 16} className="mr-1" />
          Share
        </Button>
      </div>

      {/* Share Popup Modal */}
      <Transition appear show={showSharePopup} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowSharePopup(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  {/* Header */}
                  <div className="flex justify-between items-center mb-6">
                    <Dialog.Title as="h3" className="text-lg font-heading text-gray-900">
                      Share Design
                    </Dialog.Title>
                    <button
                      onClick={() => setShowSharePopup(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Image Preview */}
                  <div className="w-20 h-16 mx-auto mb-6 rounded-xl overflow-hidden bg-gray-100 shadow-inner">
                    <img 
                      src={message.imageUrl} 
                      alt="Design preview" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  {/* Copy Success Message */}
                  {copied && (
                    <div className="mb-4 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-center">
                      <span className="text-green-700 text-sm font-medium">✓ Link copied to clipboard!</span>
                    </div>
                  )}
                  
                  {/* Share Options */}
                  <div className="flex justify-center gap-6">
                    {/* Copy Link */}
                    <div className="flex flex-col items-center">
                      <button
                        onClick={() => handleShare('copy')}
                        className={`h-14 w-14 flex items-center justify-center rounded-full transition-all duration-200 ${
                          copied 
                            ? 'bg-green-100 text-green-600' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:scale-105'
                        }`}
                      >
                        <span className="text-xl">{copied ? '✓' : '📋'}</span>
                      </button>
                      <span className="text-xs mt-2 text-gray-600 font-medium">Copy Link</span>
                    </div>
                    
                    {/* Pinterest */}
                    <div className="flex flex-col items-center">
                      <button
                        onClick={() => handleShare('pinterest')}
                        className="h-14 w-14 bg-red-600 text-white flex items-center justify-center rounded-full hover:bg-red-700 hover:scale-105 transition-all duration-200 shadow-md"
                      >
                        <span className="text-xl">📌</span>
                      </button>
                      <span className="text-xs mt-2 text-gray-600 font-medium">Pinterest</span>
                    </div>
                    
                    {/* WhatsApp */}
                    <div className="flex flex-col items-center">
                      <button
                        onClick={() => handleShare('whatsapp')}
                        className="h-14 w-14 bg-green-600 text-white flex items-center justify-center rounded-full hover:bg-green-700 hover:scale-105 transition-all duration-200 shadow-md"
                      >
                        <span className="text-xl">💬</span>
                      </button>
                      <span className="text-xs mt-2 text-gray-600 font-medium">WhatsApp</span>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default ImageFeedbackButtons; 